import { memo, useCallback, type MouseEvent } from 'react'
import { ArrowUpRight, Mic, Plus } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { LoadingState } from '@highlight/ui'
import { FadingScrollable } from '@renderer/chat'
import { useTodaysCalendarEvents } from '../../../hooks/use-upcoming-meetings'
import { useCalendars } from '@renderer/hooks/use-calendars'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useViewActions } from '../../../view-atoms'
import { MeetingCell } from '../../meeting/meeting-cell'
import { MeetingRowView } from '../../meeting/meeting-row'
import { useStartAudioNote } from '@renderer/hooks/useStartAudioNote'
import { noteNoun } from '@shared/utils/audio-notes/AudioNoteUtils'
import {
  useAudioNoteHasMeeting,
  useHasLiveAudioNote,
  useIsRecordingAudioNote,
  useIsTakingNotes,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import { useCalendarEventForCurrentMeeting } from '@renderer/hooks/audio/use-recording-calendar-event'
import { useNewMeetingContext } from '../../meeting/use-new-meeting-context'
import { useLiveMeetingIdentity } from '../../meeting/use-live-meeting-identity'
import { MEETING_VIEW_ID } from '../../meeting/meeting-view-id'
import { SIDEPANEL_CELL_SURFACE } from '../../cells/surface'
import { selectUpcomingMeetings } from './select-upcoming-meetings'
import { useHomeSectionLayout } from '../use-home-section-layout'
import { AUDIO_INDICATOR_COLOR } from '@renderer/hooks/audio/use-audio-indicator-tone'

/** Header gap the Upcoming scroller borrows above its first row so that row's shadow isn't clipped. */
const SHADOW_BLEED_PX = 6

/**
 * Persistent quick-action row above the meeting cards (per the Day 0 design's
 * Section CTA): starts a meeting note and drills into the Meeting View. While a
 * note is already live it stays put and becomes the way back into it — with no
 * calendars connected this row *is* the section body, so hiding it left Upcoming
 * empty for the whole recording.
 */
const StartMeetingNoteRow = memo(function StartMeetingNoteRow() {
  // Narrow selectors, matching SidepanelUpcomingSection below — the aggregate
  // hook re-renders this row on every audio-note-state tick during a recording,
  // which is exactly when the row is mounted. `hasLiveNote` includes `saving`,
  // so a save in flight can't be raced by starting a second recording.
  const hasActiveNote = useHasLiveAudioNote()
  const hasMeeting = useAudioNoteHasMeeting()
  const { openView } = useViewActions()
  const { startAudioNote } = useStartAudioNote()
  const newMeeting = useNewMeetingContext()
  const liveMeeting = useLiveMeetingIdentity()

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (hasActiveNote) {
        AmplitudeRenderer.trackEvent('Audio Note Action', {
          action: 'open active meeting',
          source: 'sidepanel upcoming',
        })
        openView(MEETING_VIEW_ID, liveMeeting, { sourceLabel: 'Home', pointerEvent: event })
        return
      }
      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'start taking notes',
        source: 'sidepanel upcoming',
      })
      void startAudioNote({
        source: 'assistant',
        onSuccessfulStart: () => {
          openView(MEETING_VIEW_ID, newMeeting, { sourceLabel: 'Home', pointerEvent: event })
        },
      })
    },
    [hasActiveNote, liveMeeting, newMeeting, openView, startAudioNote],
  )

  return (
    <button
      type="button"
      onClick={handleClick}
      // The shared cell surface supplies the hover container — and the
      // bg-transparent that keeps a bare <button> off Chromium's UA `buttonface`
      // grey (#efefef), which would read as a filled input rather than the flush
      // quick-action row the design asks for.
      className={twMerge('group flex w-full items-center gap-2 px-3 py-2 text-left', SIDEPANEL_CELL_SURFACE)}
    >
      {/* Quiet until the row is hovered, where the container arrives and the
          label comes up to full strength (Figma `6972:11353`). While a note is
          live the icon takes the recording green instead of waiting for
          hover, so the row reads as a recording in progress rather than an
          idle CTA. */}
      <span
        className={twMerge(
          'flex size-5 shrink-0 items-center justify-center',
          hasActiveNote ? AUDIO_INDICATOR_COLOR.live : 'text-foreground-secondary group-hover:text-foreground',
        )}
      >
        <Mic size={14} />
      </span>
      <span className="text-foreground-secondary group-hover:text-foreground type-ui-sm min-w-0 flex-1 font-normal">
        {hasActiveNote ? `View ${noteNoun(hasMeeting)}` : 'Start new audio note'}
      </span>
      <span
        aria-hidden="true"
        className="text-foreground-secondary flex size-5 shrink-0 items-center justify-center opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
      >
        {hasActiveNote ? <ArrowUpRight size={14} /> : <Plus size={14} />}
      </span>
    </button>
  )
})

export const UpcomingLoadingIndicator = memo(function UpcomingLoadingIndicator() {
  const { isFetching } = useTodaysCalendarEvents()
  if (!isFetching) return null
  return (
    <LoadingState className="text-foreground-secondary gap-1.5 text-xs">
      <LoadingState.Text messages={['Loading events']} />
      <LoadingState.Spinner className="size-3" />
    </LoadingState>
  )
})

export const SidepanelUpcomingSection = memo(function SidepanelUpcomingSection() {
  const { data: todaysEvents } = useTodaysCalendarEvents()
  const { googleAccounts, outlookAccounts, calendars } = useCalendars()
  const { upcomingMaxHeight } = useHomeSectionLayout()
  // Narrow audio-note-state slices: status/summary ticks during a recording
  // no longer re-render the whole section.
  const isRecording = useIsRecordingAudioNote()
  const isTakingNotes = useIsTakingNotes()
  // Which event the recording *is* — resolved through the shared link predicate
  // rather than the stored calendarEventId, which can still be empty while a
  // meeting is being detected (HIG-5856).
  const activeMeetingEvent = useCalendarEventForCurrentMeeting(todaysEvents)
  const activeTranscriptEventId = isTakingNotes ? (activeMeetingEvent?.id ?? null) : null
  // Gated on isRecording rather than isTakingNotes so a paused recording still
  // holds its meeting in the list once it overruns the scheduled end.
  const recordingEventId = isRecording ? (activeMeetingEvent?.id ?? null) : null

  const hasCalendars = googleAccounts.length + outlookAccounts.length > 0

  // The design leads with one detailed card and lists the rest of the day as
  // flat rows (Figma `7096:18818`) — no "N more events" collapse. Clicking the
  // section header opens the full Upcoming view, same entry point as Recents.
  const { lead, rest } = selectUpcomingMeetings(
    todaysEvents ?? [],
    activeTranscriptEventId,
    new Date(),
    recordingEventId,
  )

  return (
    // Same bleed the collapsible fold takes: a scroller clips at its padding
    // edge on BOTH axes, and its box landed flush against the cards' edges — so
    // their shadows were cut into hard lines (most visibly the happening-now
    // card's accent ring across its bottom). The 12px moves inside the clip on
    // each side and is cancelled with a negative margin: same layout, bleed room
    // back for the shadows to fall off into. Below, that lands in the fold's own
    // pb-3, which is bleed room already. The cap is +12 so the padding doesn't
    // eat into the 350px of rows the design asks for. Above, SHADOW_BLEED_PX is the
    // fold's own header gap, borrowed the same way so the first row's shadow clears
    // the top; the cap takes the same amount back.
    <FadingScrollable
      className="-mx-3 -mb-3 px-3 pb-3"
      style={{
        marginTop: -SHADOW_BLEED_PX,
        paddingTop: SHADOW_BLEED_PX,
        maxHeight: upcomingMaxHeight + SHADOW_BLEED_PX,
      }}
      fadeSize="12%"
    >
      <div className="flex flex-col gap-0.5">
        <StartMeetingNoteRow />
        {hasCalendars && lead && <MeetingCell meeting={lead} enableTitleLink showBriefPreview />}
        {/* No `content-visibility: auto` on these rows. A skipped row lays out at
            its `contain-intrinsic-size` placeholder rather than its real height,
            and base-ui measures the collapsible panel exactly once as it opens —
            so the fold animated to a placeholder-inflated 265px and then snapped
            back to the true 229px when the height var flipped to `auto`. A row
            holds no subscriptions of its own (see MeetingRowView) and the section
            caps at 350px, so there was nothing to skip. */}
        {hasCalendars &&
          rest.map((meeting) => <MeetingRowView key={meeting.id} meeting={meeting} calendars={calendars} />)}
      </div>
    </FadingScrollable>
  )
})
